import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Table, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Nav, NavItem, NavLink } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../../../Config/api';
import { useOrderStatuses, statusColor } from '../../../../Config/orderStatuses';

// Order currency display. Prices are stored per currency on the order line, so
// nothing here converts between them — it only picks the right symbol/field.
const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };
const symbolFor = (currency) => CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS.EUR;
// Shipping is quoted separately from the goods and isn't always billed in the
// order's currency, so it carries its own. The order's own currency is always
// offered too, so a USD order can keep shipping in USD.
const SHIPPING_CURRENCIES = ['EUR', 'GBP'];
const shippingCurrencyOptions = (orderCurrency) =>
  [...new Set([...SHIPPING_CURRENCIES, orderCurrency].filter(Boolean))];
const REORDER_PRICE_FIELDS = { USD: 'ws_usd_price', GBP: 'ws_gbp_price' };
const reorderUnitPrice = (item, currency) => {
  const field = REORDER_PRICE_FIELDS[currency];
  const priced = field ? parseFloat(item[field]) : NaN;
  return (!isNaN(priced) && priced > 0) ? priced : (parseFloat(item.wholesale_price) || 0);
};
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// order_date is a plain "YYYY-MM-DD" calendar date with no time-of-day or
// timezone. `new Date(dateString)` parses a date-only string as UTC midnight
// per spec, so any later local-timezone read (.getMonth(), toLocaleDateString,
// ...) can land on the wrong calendar day depending on the viewer's offset —
// e.g. a UTC-stored Oct 1 can read back as Sep 30 in a negative-offset zone.
// Building the Date from its literal Y/M/D components instead sidesteps that
// entirely: it's always that same calendar day, in every timezone.
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  // Anchored at both ends so a full ISO datetime (e.g. shipped_date, which
  // has a real time-of-day that genuinely needs timezone conversion) falls
  // through to new Date() unchanged — only a bare "YYYY-MM-DD" takes the
  // local-components path.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (!match) return new Date(dateString);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const AllOrders = () => {
  const orderStatuses = useOrderStatuses();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [depositModal, setDepositModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [depositType, setDepositType] = useState('percentage');
  const [depositValue, setDepositValue] = useState('');
  const [shippingCostModal, setShippingCostModal] = useState(false);
  const [shippingCostValue, setShippingCostValue] = useState('');
  const [shippingCostCurrency, setShippingCostCurrency] = useState('EUR');
  const [shippingOrder, setShippingOrder] = useState(null);
  const [shippingModal, setShippingModal] = useState(false);
  const [shippingDate, setShippingDate] = useState('');
  const [shippingDocuments, setShippingDocuments] = useState([]);
  const [trackingLink, setTrackingLink] = useState('');
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [proofOfPayment, setProofOfPayment] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [discountModal, setDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('');
  // Newest order first by default.
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [monthFilter, setMonthFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('active');

  // Shipment tag states
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [shipmentTagModal, setShipmentTagModal] = useState(false);

  // Bulk deposit states
  const [bulkDepositModal, setBulkDepositModal] = useState(false);
  const [bulkDepositType, setBulkDepositType] = useState('percentage');
  const [bulkDepositValue, setBulkDepositValue] = useState('');

  // Bulk status states
  const [bulkStatusModal, setBulkStatusModal] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [bulkAwaitingReason, setBulkAwaitingReason] = useState('');
  const [awaitingModal, setAwaitingModal] = useState(false);
  const [awaitingOrderId, setAwaitingOrderId] = useState(null);
  const [awaitingReason, setAwaitingReason] = useState('');
  const [shipmentTags, setShipmentTags] = useState([]);
  const [selectedShipmentTag, setSelectedShipmentTag] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagMonth, setNewTagMonth] = useState('');
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [shipmentTagFilter, setShipmentTagFilter] = useState('All');

  // Packaging list states
  const [packagingListModal, setPackagingListModal] = useState(false);
  const [packagingPreview, setPackagingPreview] = useState(null);
  const [packagingLoading, setPackagingLoading] = useState(false);
  const [generatingPdfs, setGeneratingPdfs] = useState(false);

  // Cancel items & credit note states
  const [cancelItemsModal, setCancelItemsModal] = useState(false);
  const [cancelOrderItems, setCancelOrderItems] = useState([]);
  const [cancelItemsLoading, setCancelItemsLoading] = useState(false);
  const [cancelItemsGenerating, setCancelItemsGenerating] = useState(false);
  const [selectedCancelItems, setSelectedCancelItems] = useState({});
  const [creditNoteShipping, setCreditNoteShipping] = useState('');

  // Reorder states
  const [reorderModal, setReorderModal] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderGenerating, setReorderGenerating] = useState(false);
  const [selectedReorderItems, setSelectedReorderItems] = useState({});

  // Custom CSS to fix table hover text color issue
  const tableStyles = `
    .all-orders-table.table-striped.table-hover tbody tr:hover th,
    .all-orders-table.table-striped.table-hover tbody tr:hover td {
      color: #212529 !important;
      background-color: rgba(0, 0, 0, 0.075);
    }
    .order-id-link {
      color: #007bff !important;
      text-decoration: none !important;
      font-weight: bold !important;
      cursor: pointer;
    }
    .order-id-link:hover {
      color: #0056b3 !important;
      text-decoration: underline !important;
    }
    .all-orders-table.table-striped.table-hover tbody tr:hover .order-id-link {
      color: #0056b3 !important;
    }
    .sortable-header {
      cursor: pointer;
      user-select: none;
    }
    .sortable-header:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    .selected-row {
      background-color: rgba(0, 123, 255, 0.15) !important;
    }
    .shipment-tag-badge {
      font-size: 11px;
      padding: 4px 8px;
    }
    .order-tabs .nav-link {
      cursor: pointer;
      padding: 10px 20px;
      font-weight: 500;
    }
    .order-tabs .nav-link.active {
      background-color: #007bff;
      color: white !important;
      border-radius: 4px;
    }
    .order-tabs .nav-link:not(.active):hover {
      background-color: #f8f9fa;
    }
    .status-tabs {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 10px 0;
    }
    .status-tab {
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      cursor: pointer;
      border: 1px solid #dee2e6;
      background-color: #f8f9fa;
      transition: all 0.2s;
    }
    .status-tab:hover {
      background-color: #e9ecef;
    }
    .status-tab.active {
      background-color: #007bff;
      color: white;
      border-color: #007bff;
    }
    .status-tab.status-all.active { background-color: #6c757d; border-color: #6c757d; }
    .status-tab.status-new-order.active { background-color: #007bff; border-color: #007bff; }
    .status-tab.status-in-production.active { background-color: #6c757d; border-color: #6c757d; }
    .status-tab.status-ready-to-ship.active { background-color: #17a2b8; border-color: #17a2b8; }
    .status-tab.status-shipped.active { background-color: #17a2b8; border-color: #17a2b8; }
    .status-tab.status-delivered.active { background-color: #28a745; border-color: #28a745; }
    .status-tab.status-deposit-paid.active { background-color: #ffc107; border-color: #ffc107; color: #212529; }
    .status-tab.status-paid-in-full.active { background-color: #28a745; border-color: #28a745; }
    .status-tab.status-cancelled.active { background-color: #dc3545; border-color: #dc3545; }
  `;

  useEffect(() => {
    fetchOrders();
    fetchShipmentTags();
  }, []);

  const fetchShipmentTags = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/shipment-tags`);
      setShipmentTags(response.data.tags || []);
    } catch (err) {
      console.error('Error fetching shipment tags:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/all-orders`);
      console.log('Fetched orders:', response.data.orders);

      // Fetch deposit and shipping cost information for each order
      const ordersWithDepositsAndShipping = await Promise.all(
        response.data.orders.map(async (order) => {
          try {
            const depositResponse = await axios.get(`${API_BASE_URL}/api/order-deposit/${order.order_id}`);
            const shippingCostResponse = await axios.get(`${API_BASE_URL}/api/order-shipping-cost/${order.order_id}`);
            const discountResponse = await axios.get(`${API_BASE_URL}/api/order-discount/${order.order_id}`);
            return {
              ...order,
              deposit: depositResponse.data.deposit,
              shippingCost: shippingCostResponse.data.shippingCost,
              shippingCurrency: shippingCostResponse.data.shippingCurrency,
              discount: discountResponse.data.discount
            };
          } catch (err) {
            console.error(`Error fetching data for order ${order.order_id}:`, err);
            return order;
          }
        })
      );

      setOrders(ordersWithDepositsAndShipping);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedOrders = (ordersToSort) => {
    if (!sortConfig.key) {
      return ordersToSort;
    }

    return [...ordersToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'order_id':
          aValue = a.order_id;
          bValue = b.order_id;
          break;
        case 'created_at':
          aValue = a.created_at ? parseLocalDate(a.created_at).getTime() : 0;
          bValue = b.created_at ? parseLocalDate(b.created_at).getTime() : 0;
          break;
        case 'store_name':
          aValue = (a.store_name || '').toLowerCase();
          bValue = (b.store_name || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || 'Production').toLowerCase();
          bValue = (b.status || 'Production').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      // Orders placed on the same day keep a steady order: by order number,
      // in the same direction as the date.
      if (sortConfig.key === 'created_at') {
        const byOrderId = String(a.order_id || '').localeCompare(String(b.order_id || ''), undefined, { numeric: true });
        return sortConfig.direction === 'asc' ? byOrderId : -byOrderId;
      }
      return 0;
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return ' ⇅';
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Distinct years present in the data, newest first — built from the orders
  // themselves so it never needs manual updating as new orders come in.
  // Months are a fixed Jan-Dec list (MONTH_NAMES) since, unlike years,
  // they're not something new data introduces.
  const getAvailableYears = () => {
    const years = new Set();
    orders.forEach(order => {
      if (!order.created_at) return;
      const d = parseLocalDate(order.created_at);
      if (d && !isNaN(d.getTime())) years.add(d.getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  const getFilteredOrders = (ordersToFilter) => {
    let filtered = ordersToFilter;

    // Filter by tab
    if (activeTab === 'active') {
      filtered = filtered.filter(order => order.status !== 'Cancelled');
    } else if (activeTab === 'cancelled') {
      filtered = filtered.filter(order => order.status === 'Cancelled');
    }

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filter by shipment tag
    if (shipmentTagFilter !== 'All') {
      if (shipmentTagFilter === 'None') {
        filtered = filtered.filter(order => !order.shipment_tag_id);
      } else {
        filtered = filtered.filter(order => order.shipment_tag_id === parseInt(shipmentTagFilter));
      }
    }

    // Filter by order month/year — independent selectors, so either can be
    // picked alone (e.g. "every January" or "all of 2025") or combined.
    if (monthFilter !== 'All' || yearFilter !== 'All') {
      filtered = filtered.filter(order => {
        if (!order.created_at) return false;
        const d = parseLocalDate(order.created_at);
        if (!d || isNaN(d.getTime())) return false;
        const monthOk = monthFilter === 'All' || (d.getMonth() + 1) === parseInt(monthFilter, 10);
        const yearOk = yearFilter === 'All' || d.getFullYear() === parseInt(yearFilter, 10);
        return monthOk && yearOk;
      });
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(order => {
        return (
          order.order_id?.toLowerCase().includes(searchLower) ||
          order.store_name?.toLowerCase().includes(searchLower) ||
          order.email?.toLowerCase().includes(searchLower) ||
          order.status?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  };

  // Get counts for tabs
  const getTabCounts = () => {
    const all = orders.length;
    const active = orders.filter(order => order.status !== 'Cancelled').length;
    const cancelled = orders.filter(order => order.status === 'Cancelled').length;
    return { all, active, cancelled };
  };

  // Get counts for status tabs (based on current active tab filter)
  const getStatusCounts = () => {
    let filteredByTab = orders;
    if (activeTab === 'active') {
      filteredByTab = orders.filter(order => order.status !== 'Cancelled');
    } else if (activeTab === 'cancelled') {
      filteredByTab = orders.filter(order => order.status === 'Cancelled');
    }

    return {
      all: filteredByTab.length,
      'New Order': filteredByTab.filter(o => o.status === 'New Order' || !o.status).length,
      'Awaiting Action': filteredByTab.filter(o => o.status === 'Awaiting Action').length,
      'Confirmed': filteredByTab.filter(o => o.status === 'Confirmed').length,
      'In Production': filteredByTab.filter(o => o.status === 'In Production').length,
      'Ready to Ship': filteredByTab.filter(o => o.status === 'Ready to Ship').length,
      'Shipped': filteredByTab.filter(o => o.status === 'Shipped').length,
      'Delivered': filteredByTab.filter(o => o.status === 'Delivered').length,
      'Deposit Paid': filteredByTab.filter(o => o.status === 'Deposit Paid').length,
      'Paid in Full': filteredByTab.filter(o => o.status === 'Paid in Full').length,
      'Cancelled': filteredByTab.filter(o => o.status === 'Cancelled').length
    };
  };

  // Selection handlers
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAll = (filteredOrders) => {
    const filteredOrderIds = filteredOrders.map(o => o.order_id);
    if (selectedOrders.length === filteredOrderIds.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrderIds);
    }
  };

  const toggleShipmentTagModal = () => {
    setShipmentTagModal(!shipmentTagModal);
    if (shipmentTagModal) {
      setSelectedShipmentTag('');
      setShowNewTagForm(false);
      setNewTagName('');
      setNewTagMonth('');
    }
  };

  const handleCreateNewTag = async () => {
    if (!newTagName || !newTagMonth) {
      alert('Please enter tag name and month');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/shipment-tags`, {
        name: newTagName,
        month: newTagMonth
      });

      setShipmentTags([response.data.tag, ...shipmentTags]);
      setSelectedShipmentTag(response.data.tag.id.toString());
      setShowNewTagForm(false);
      setNewTagName('');
      setNewTagMonth('');
      alert('Shipment tag created successfully!');
    } catch (err) {
      console.error('Error creating shipment tag:', err);
      alert('Failed to create shipment tag');
    }
  };

  const handleAssignShipmentTag = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/assign-shipment-tag`, {
        orderIds: selectedOrders,
        shipmentTagId: selectedShipmentTag ? parseInt(selectedShipmentTag) : null
      });

      // Update local state
      const tag = shipmentTags.find(t => t.id === parseInt(selectedShipmentTag));
      const updatedOrders = orders.map(order => {
        if (selectedOrders.includes(order.order_id)) {
          return {
            ...order,
            shipment_tag_id: selectedShipmentTag ? parseInt(selectedShipmentTag) : null,
            shipment_tag_name: tag?.name || null,
            shipment_tag_month: tag?.month || null,
            // Auto-set status to 'In Production' when assigning a tag
            status: selectedShipmentTag ? 'In Production' : order.status
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      setSelectedOrders([]);
      toggleShipmentTagModal();
      alert(`Shipment tag ${selectedShipmentTag ? 'assigned' : 'removed'} successfully!`);
    } catch (err) {
      console.error('Error assigning shipment tag:', err);
      alert('Failed to assign shipment tag');
    }
  };

  const handleViewPI = (orderId) => {
    // Open PI HTML in new tab
    window.open(`${API_BASE_URL}/api/generate-pi/${orderId}`, '_blank');
  };

  const handleDownloadExcel = (orderId) => {
    // Download Excel PI
    window.open(`${API_BASE_URL}/api/generate-excel-pi/${orderId}`, '_blank');
  };

  const handleDownloadPDF = (orderId) => {
    // Download PDF PI
    window.open(`${API_BASE_URL}/api/generate-pdf-pi/${orderId}`, '_blank');
  };

  const handleDownloadPDFWithHSN = (orderId) => {
    // Download PDF PI with HSN codes
    window.open(`${API_BASE_URL}/api/generate-pdf-pi-with-hsn/${orderId}`, '_blank');
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(`Are you sure you want to delete order ${orderId}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingOrder(orderId);
      const response = await axios.delete(`${API_BASE_URL}/api/delete-order/${orderId}`);

      if (response.data.message) {
        // Remove the deleted order from the local state
        setOrders(orders.filter(order => order.order_id !== orderId));
        alert(`Order ${orderId} deleted successfully. ${response.data.deletedItemsCount} items were removed.`);
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      alert(`Failed to delete order: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingOrder(null);
    }
  };

  const toggleDepositModal = () => {
    setDepositModal(!depositModal);
    if (depositModal) {
      setSelectedOrder(null);
      setDepositValue('');
      setDepositType('percentage');
    }
  };

  const toggleBulkDepositModal = () => {
    setBulkDepositModal(!bulkDepositModal);
    if (bulkDepositModal) {
      setBulkDepositValue('');
      setBulkDepositType('percentage');
    }
  };

  const handleSaveBulkDeposit = async () => {
    if (!bulkDepositValue || parseFloat(bulkDepositValue) <= 0) {
      alert('Please enter a valid deposit value');
      return;
    }

    if (bulkDepositType === 'percentage' && parseFloat(bulkDepositValue) > 100) {
      alert('Percentage value cannot exceed 100%');
      return;
    }

    try {
      // Save deposit for each selected order
      await Promise.all(
        selectedOrders.map(orderId =>
          axios.post(`${API_BASE_URL}/api/order-deposit`, {
            orderId: orderId,
            type: bulkDepositType,
            value: parseFloat(bulkDepositValue)
          })
        )
      );

      // Update local state for all selected orders
      const updatedOrders = orders.map(order => {
        if (selectedOrders.includes(order.order_id)) {
          return {
            ...order,
            deposit: {
              deposit_type: bulkDepositType,
              deposit_value: parseFloat(bulkDepositValue),
              order_id: order.order_id
            }
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert(`Deposit added to ${selectedOrders.length} order(s) successfully!`);
      toggleBulkDepositModal();
      setSelectedOrders([]);
    } catch (err) {
      console.error(err);
      alert('Error adding bulk deposit: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleBulkStatusModal = () => {
    setBulkStatusModal(!bulkStatusModal);
    if (bulkStatusModal) {
      setBulkStatusValue('');
      setBulkAwaitingReason('');
    }
  };

  const handleSaveBulkStatus = async () => {
    if (!bulkStatusValue) {
      alert('Please select a status');
      return;
    }
    if (bulkStatusValue === 'Awaiting Action' && !bulkAwaitingReason.trim()) {
      alert('Please enter a reason for Awaiting Action');
      return;
    }

    try {
      // Update status for each selected order
      await Promise.all(
        selectedOrders.map(orderId =>
          axios.put(`${API_BASE_URL}/api/update-order-status/${orderId}`, {
            status: bulkStatusValue,
            ...(bulkStatusValue === 'Awaiting Action' ? { reason: bulkAwaitingReason.trim() } : {})
          })
        )
      );

      // Update local state for all selected orders
      const updatedOrders = orders.map(order => {
        if (selectedOrders.includes(order.order_id)) {
          return {
            ...order,
            status: bulkStatusValue,
            awaiting_action_reason: bulkStatusValue === 'Awaiting Action' ? bulkAwaitingReason.trim() : null,
            status_changed_at: order.status === bulkStatusValue ? order.status_changed_at : new Date().toISOString()
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert(`Status updated to "${bulkStatusValue}" for ${selectedOrders.length} order(s) successfully!`);
      toggleBulkStatusModal();
      setSelectedOrders([]);
    } catch (err) {
      console.error(err);
      alert('Error updating bulk status: ' + (err.response?.data?.error || err.message));
    }
  };

  // Packaging List handlers
  const handleOpenPackagingListModal = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order');
      return;
    }

    setPackagingLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/packaging-list/preview`, {
        orderIds: selectedOrders
      });
      setPackagingPreview(response.data);
      setPackagingListModal(true);
    } catch (err) {
      console.error('Error fetching packaging list preview:', err);
      alert('Failed to generate packaging list preview: ' + (err.response?.data?.error || err.message));
    } finally {
      setPackagingLoading(false);
    }
  };

  const togglePackagingListModal = () => {
    setPackagingListModal(!packagingListModal);
    if (packagingListModal) {
      setPackagingPreview(null);
    }
  };

  const handleGeneratePackagingLists = async () => {
    if (!packagingPreview) return;

    setGeneratingPdfs(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/packaging-list/generate`, {
        orderIds: packagingPreview.orderIds,
        allocations: packagingPreview.allocations
      });

      if (response.data.success) {
        alert(`Successfully generated ${response.data.packagingLists.length} packaging list(s)!`);

        // Open PDF download links for each generated list
        response.data.packagingLists.forEach((list, index) => {
          setTimeout(() => {
            window.open(`${API_BASE_URL}/api/packaging-list/${list.id}/pdf`, '_blank');
          }, index * 500);
        });

        togglePackagingListModal();
        setSelectedOrders([]);
      }
    } catch (err) {
      console.error('Error generating packaging lists:', err);
      alert('Failed to generate packaging lists: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingPdfs(false);
    }
  };

  // Cancel items handlers
  const handleOpenCancelItems = async (order) => {
    setSelectedOrder(order);
    setCancelItemsLoading(true);
    setCancelItemsModal(true);
    setSelectedCancelItems({});
    setCancelOrderItems([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/order-items-for-cancel/${order.order_id}`);
      setCancelOrderItems(response.data.items || []);
    } catch (err) {
      console.error('Error fetching order items for cancel:', err);
      alert('Failed to load order items: ' + (err.response?.data?.error || err.message));
    } finally {
      setCancelItemsLoading(false);
    }
  };

  const toggleCancelItemsModal = () => {
    setCancelItemsModal(!cancelItemsModal);
    if (cancelItemsModal) {
      setSelectedCancelItems({});
      setCancelOrderItems([]);
      setSelectedOrder(null);
      setCreditNoteShipping('');
    }
  };

  const handleToggleCancelItem = (productId, size) => {
    const key = `${productId}_${size}`;
    setSelectedCancelItems(prev => {
      const current = prev[key];
      if (current?.selected) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      const item = cancelOrderItems.find(i => i.product_id === productId && i.size === size);
      const defaultQty = item?.available_qty || 1;
      return { ...prev, [key]: { selected: true, cancel_qty: defaultQty } };
    });
  };

  const handleCancelQtyChange = (productId, size, qty) => {
    const key = `${productId}_${size}`;
    const item = cancelOrderItems.find(i => i.product_id === productId && i.size === size);
    const maxQty = item?.available_qty || 1;
    const parsed = parseInt(qty);

    setSelectedCancelItems(prev => {
      // Empty input or 0 -> deselect the row
      if (qty === '' || isNaN(parsed) || parsed <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      const validQty = Math.min(parsed, maxQty);
      return { ...prev, [key]: { selected: true, cancel_qty: validQty } };
    });
  };

  const handleToggleAllCancelItems = () => {
    const allSelected = cancelOrderItems.length > 0 && cancelOrderItems.every(item => {
      const key = `${item.product_id}_${item.size}`;
      return selectedCancelItems[key]?.selected;
    });

    if (allSelected) {
      setSelectedCancelItems({});
    } else {
      const newSelected = {};
      cancelOrderItems.forEach(item => {
        const key = `${item.product_id}_${item.size}`;
        newSelected[key] = {
          selected: true,
          cancel_qty: selectedCancelItems[key]?.cancel_qty ?? (parseInt(item.available_qty, 10) || 1)
        };
      });
      setSelectedCancelItems(newSelected);
    }
  };

  const getCreditTotal = () => {
    let total = 0;
    let count = 0;
    Object.entries(selectedCancelItems).forEach(([key, val]) => {
      if (val.selected) {
        const [productId, size] = key.split('_');
        const item = cancelOrderItems.find(i => String(i.product_id) === productId && i.size === size);
        if (item) {
          total += (parseFloat(item.wholesale_price) || 0) * val.cancel_qty;
          count += val.cancel_qty;
        }
      }
    });
    return { total, count };
  };

  const handleCancelItemsOnly = async () => {
    const selectedItems = Object.entries(selectedCancelItems)
      .filter(([, val]) => val.selected)
      .map(([key, val]) => {
        const [productId, size] = key.split('_');
        return { product_id: parseInt(productId), size, cancel_qty: val.cancel_qty };
      });

    if (selectedItems.length === 0) {
      alert('Please enter a quantity for at least one item to cancel');
      return;
    }

    const { count } = getCreditTotal();
    if (!window.confirm(
      `Cancel ${count} item(s) from order ${selectedOrder.order_id}?\n\nNo credit note will be generated. This action cannot be undone.`
    )) {
      return;
    }

    setCancelItemsGenerating(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/cancel-items-only/${selectedOrder.order_id}`,
        { items: selectedItems }
      );
      alert(`${count} item(s) cancelled successfully (no credit note generated).`);
      toggleCancelItemsModal();
      fetchOrders();
    } catch (err) {
      console.error('Error cancelling items:', err);
      alert('Failed to cancel items: ' + (err.response?.data?.error || err.message));
    } finally {
      setCancelItemsGenerating(false);
    }
  };

  const handleGenerateCreditNote = async () => {
    const selectedItems = Object.entries(selectedCancelItems)
      .filter(([, val]) => val.selected)
      .map(([key, val]) => {
        const [productId, size] = key.split('_');
        return { product_id: parseInt(productId), size, cancel_qty: val.cancel_qty };
      });

    if (selectedItems.length === 0) {
      alert('Please select at least one item to cancel');
      return;
    }

    const { total, count } = getCreditTotal();
    const shippingAmount = parseFloat(creditNoteShipping) || 0;
    const grandTotal = total + shippingAmount;
    const shippingLine = shippingAmount > 0 ? ` (incl. €${shippingAmount.toFixed(2)} shipping)` : '';
    if (!window.confirm(`Cancel ${count} item(s) and generate a credit note for €${grandTotal.toFixed(2)}${shippingLine}?\n\nThis action cannot be undone.`)) {
      return;
    }

    setCancelItemsGenerating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/cancel-items-credit-note/${selectedOrder.order_id}`,
        { items: selectedItems, shipping_amount: shippingAmount },
        { responseType: 'blob' }
      );

      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename="')[1]?.replace('"', '') : `CreditNote_${selectedOrder.order_id}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Credit note generated and downloaded successfully!');
      toggleCancelItemsModal();
      fetchOrders();
    } catch (err) {
      console.error('Error generating credit note:', err);
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          alert('Error: ' + (json.error || json.details || 'Failed to generate credit note'));
        } catch {
          alert('Failed to generate credit note');
        }
      } else {
        alert('Failed to generate credit note: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setCancelItemsGenerating(false);
    }
  };

  // Reorder handlers
  const handleOpenReorder = async (order) => {
    setSelectedOrder(order);
    setReorderLoading(true);
    setReorderModal(true);
    setSelectedReorderItems({});
    setReorderItems([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/order-items-for-reorder/${order.order_id}`);
      setReorderItems(response.data.items || []);
    } catch (err) {
      console.error('Error fetching order items for reorder:', err);
      alert('Failed to load order items: ' + (err.response?.data?.error || err.message));
    } finally {
      setReorderLoading(false);
    }
  };

  const toggleReorderModal = () => {
    setReorderModal(!reorderModal);
    if (reorderModal) {
      setSelectedReorderItems({});
      setReorderItems([]);
      setSelectedOrder(null);
    }
  };

  const handleToggleReorderItem = (productId, size) => {
    const key = `${productId}_${size}`;
    setSelectedReorderItems(prev => {
      const current = prev[key];
      if (current?.selected) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      const item = reorderItems.find(i => i.product_id === productId && i.size === size);
      return { ...prev, [key]: { selected: true, reorder_qty: item?.quantity || 1 } };
    });
  };

  const handleReorderQtyChange = (productId, size, qty) => {
    const key = `${productId}_${size}`;
    const parsed = parseInt(qty);

    setSelectedReorderItems(prev => {
      if (qty === '' || isNaN(parsed) || parsed <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { selected: true, reorder_qty: parsed } };
    });
  };

  const handleToggleAllReorderItems = () => {
    const allSelected = reorderItems.length > 0 && reorderItems.every(item => {
      const key = `${item.product_id}_${item.size}`;
      return selectedReorderItems[key]?.selected;
    });

    if (allSelected) {
      setSelectedReorderItems({});
    } else {
      const newSelected = {};
      reorderItems.forEach(item => {
        const key = `${item.product_id}_${item.size}`;
        newSelected[key] = {
          selected: true,
          reorder_qty: selectedReorderItems[key]?.reorder_qty || item.quantity || 1
        };
      });
      setSelectedReorderItems(newSelected);
    }
  };

  const getReorderTotal = () => {
    let total = 0;
    let count = 0;
    Object.entries(selectedReorderItems).forEach(([key, val]) => {
      if (val.selected) {
        const [productId, size] = key.split('_');
        const item = reorderItems.find(i => String(i.product_id) === productId && i.size === size);
        if (item) {
          const unitPrice = reorderUnitPrice(item, selectedOrder?.currency);
          total += unitPrice * val.reorder_qty;
          count += val.reorder_qty;
        }
      }
    });
    return { total, count };
  };

  const handleGenerateReorder = async () => {
    const selectedItems = Object.entries(selectedReorderItems)
      .filter(([, val]) => val.selected)
      .map(([key, val]) => {
        const [productId, size] = key.split('_');
        return { product_id: parseInt(productId), size, quantity: val.reorder_qty };
      });

    if (selectedItems.length === 0) {
      alert('Please select at least one item to reorder');
      return;
    }

    const { total, count } = getReorderTotal();
    const symbol = symbolFor(selectedOrder?.currency);
    if (!window.confirm(
      `Create a new reorder with ${count} item(s) (Total: ${symbol}${total.toFixed(2)})?\n\nA new order will be created and the PI will open in a new tab.`
    )) {
      return;
    }

    setReorderGenerating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/reorder/${selectedOrder.order_id}`,
        { items: selectedItems }
      );

      const newOrderId = response.data.order_id;
      alert(`Reorder created successfully!\nNew Order ID: ${newOrderId}`);
      toggleReorderModal();
      fetchOrders();

      // Open the PI for the new order in a new tab
      window.open(`${API_BASE_URL}/api/generate-pi/${newOrderId}`, '_blank');
    } catch (err) {
      console.error('Error creating reorder:', err);
      alert('Failed to create reorder: ' + (err.response?.data?.error || err.message));
    } finally {
      setReorderGenerating(false);
    }
  };

  const handleAddDeposit = (order) => {
    setSelectedOrder(order);
    setDepositModal(true);
  };

  const handleSaveDeposit = async () => {
    if (!depositValue || parseFloat(depositValue) <= 0) {
      alert('Please enter a valid deposit value');
      return;
    }

    if (depositType === 'percentage' && parseFloat(depositValue) > 100) {
      alert('Percentage value cannot exceed 100%');
      return;
    }

    const depositData = {
      orderId: selectedOrder.order_id,
      type: depositType,
      value: parseFloat(depositValue)
    };

    try {
      await axios.post(`${API_BASE_URL}/api/order-deposit`, depositData);

      // Update local data
      const updatedOrders = orders.map(order => {
        if (order.order_id === selectedOrder.order_id) {
          return {
            ...order,
            deposit: {
              deposit_type: depositType,
              deposit_value: parseFloat(depositValue),
              order_id: selectedOrder.order_id
            }
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert('Deposit added successfully!');
      toggleDepositModal();
    } catch (err) {
      console.error(err);
      alert('Error adding deposit: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveDeposit = async () => {
    if (!window.confirm(`Are you sure you want to remove the deposit for order ${selectedOrder?.order_id}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/order-deposit/${selectedOrder.order_id}`);

      // Update local data
      const updatedOrders = orders.map(order => {
        if (order.order_id === selectedOrder.order_id) {
          return {
            ...order,
            deposit: null
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert('Deposit removed successfully!');
      toggleDepositModal();
    } catch (err) {
      console.error(err);
      alert('Error removing deposit: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleShippingCostModal = () => {
    setShippingCostModal(!shippingCostModal);
    if (shippingCostModal) {
      setSelectedOrder(null);
      setShippingCostValue('');
      setShippingCostCurrency('EUR');
    }
  };

  const handleAddShippingCost = (order) => {
    setSelectedOrder(order);
    setShippingCostValue(order.shippingCost || '50.00');
    setShippingCostCurrency(order.shippingCurrency || order.currency || 'EUR');
    setShippingCostModal(true);
  };

  const handleSaveShippingCost = async () => {
    if (!shippingCostValue || parseFloat(shippingCostValue) < 0) {
      alert('Please enter a valid shipping cost value');
      return;
    }

    const shippingCostData = {
      orderId: selectedOrder.order_id,
      shippingCost: parseFloat(shippingCostValue),
      shippingCurrency: shippingCostCurrency
    };

    try {
      await axios.post(`${API_BASE_URL}/api/order-shipping-cost`, shippingCostData);

      // Update local data
      const updatedOrders = orders.map(order => {
        if (order.order_id === selectedOrder.order_id) {
          return {
            ...order,
            shippingCost: parseFloat(shippingCostValue),
            shippingCurrency: shippingCostCurrency
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert('Shipping cost updated successfully!');
      toggleShippingCostModal();
    } catch (err) {
      console.error(err);
      alert('Error updating shipping cost: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleShippingModal = () => {
    setShippingModal(!shippingModal);
    if (shippingModal) {
      setSelectedOrder(null);
      setShippingDate('');
      setShippingDocuments([]);
      setTrackingLink('');
    }
  };

  const toggleDiscountModal = () => {
    setDiscountModal(!discountModal);
    if (discountModal) {
      setSelectedOrder(null);
      setDiscountValue('');
      setDiscountType('percentage');
    }
  };

  const handleAddDiscount = (order) => {
    setSelectedOrder(order);
    if (order.discount) {
      setDiscountType(order.discount.discount_type);
      setDiscountValue(order.discount.discount_value.toString());
    } else {
      setDiscountType('percentage');
      setDiscountValue('');
    }
    setDiscountModal(true);
  };

  const handleSaveDiscount = async () => {
    if (discountValue === '' || discountValue === null || discountValue === undefined || parseFloat(discountValue) < 0) {
      alert('Please enter a valid discount value');
      return;
    }

    if (discountType === 'percentage' && parseFloat(discountValue) > 100) {
      alert('Percentage value cannot exceed 100%');
      return;
    }

    const parsedValue = parseFloat(discountValue);

    // If value is 0, remove the discount
    if (parsedValue === 0) {
      try {
        await axios.delete(`${API_BASE_URL}/api/order-discount/${selectedOrder.order_id}`);

        const updatedOrders = orders.map(order => {
          if (order.order_id === selectedOrder.order_id) {
            const { discount, ...rest } = order;
            return rest;
          }
          return order;
        });

        setOrders(updatedOrders);
        alert('Discount removed successfully!');
        toggleDiscountModal();
        return;
      } catch (err) {
        console.error(err);
        alert('Error removing discount: ' + (err.response?.data?.error || err.message));
        return;
      }
    }

    const discountData = {
      orderId: selectedOrder.order_id,
      type: discountType,
      value: parsedValue
    };

    try {
      await axios.post(`${API_BASE_URL}/api/order-discount`, discountData);

      // Update local data
      const updatedOrders = orders.map(order => {
        if (order.order_id === selectedOrder.order_id) {
          return {
            ...order,
            discount: {
              discount_type: discountType,
              discount_value: parsedValue,
              order_id: selectedOrder.order_id
            }
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      alert('Discount added successfully!');
      toggleDiscountModal();
    } catch (err) {
      console.error(err);
      alert('Error adding discount: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveDiscount = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/order-discount/${selectedOrder.order_id}`);

      const updatedOrders = orders.map(order => {
        if (order.order_id === selectedOrder.order_id) {
          const { discount, ...rest } = order;
          return rest;
        }
        return order;
      });

      setOrders(updatedOrders);
      alert('Discount removed successfully!');
      toggleDiscountModal();
    } catch (err) {
      console.error(err);
      alert('Error removing discount: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleMarkAsShipped = (order) => {
    setSelectedOrder(order);
    setShippingDate(''); // Reset to empty, user must select a date
    setShippingModal(true);
  };

  const handleSaveShipping = async () => {
    if (!shippingDate) {
      alert('Please select a shipping date');
      return;
    }

    try {
      setShippingOrder(selectedOrder.order_id);

      const formData = new FormData();
      formData.append('shippingDate', shippingDate);
      formData.append('trackingLink', trackingLink);

      // Append all shipping documents if any
      if (shippingDocuments.length > 0) {
        shippingDocuments.forEach((file) => {
          formData.append('shippingDocuments', file);
        });
      }

      const response = await axios.post(`${API_BASE_URL}/api/mark-order-shipped/${selectedOrder.order_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.message) {
        // Update the local state to reflect the shipped status
        const updatedOrders = orders.map(order => {
          if (order.order_id === selectedOrder.order_id) {
            return {
              ...order,
              is_shipped: 1,
              shipped_date: shippingDate
            };
          }
          return order;
        });

        setOrders(updatedOrders);
        alert(`Order ${selectedOrder.order_id} has been marked as shipped and an invoice has been created.`);
        toggleShippingModal();
      }
    } catch (err) {
      console.error('Error marking order as shipped:', err);
      alert(`Failed to mark order as shipped: ${err.response?.data?.error || err.message}`);
    } finally {
      setShippingOrder(null);
    }
  };

  const togglePaymentModal = () => {
    setPaymentModal(!paymentModal);
    if (paymentModal) {
      setSelectedOrder(null);
      setPaymentDate('');
      setPaymentAmount('');
      setProofOfPayment(null);
      setPaymentNotes('');
      setPaymentType('');
    }
  };

  const handleMarkPaid = (order) => {
    setSelectedOrder(order);
    setPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (savingPayment) return;
    if (!paymentType) {
      alert('Please select a payment type');
      return;
    }

    if (!paymentDate) {
      alert('Please select a payment date');
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    setSavingPayment(true);
    try {
      const formData = new FormData();
      formData.append('paymentType', paymentType);
      formData.append('paymentDate', paymentDate);
      formData.append('paymentAmount', paymentAmount);
      if (proofOfPayment) {
        formData.append('proofOfPayment', proofOfPayment);
      }
      formData.append('notes', paymentNotes);

      const response = await axios.post(`${API_BASE_URL}/api/mark-invoice-paid/${selectedOrder.order_id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Refresh orders to get updated payment status
      await fetchOrders();

      alert(response.data.message || 'Payment recorded successfully!');
      togglePaymentModal();
    } catch (err) {
      console.error(err);
      alert('Error marking as paid: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingPayment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Date and time the order entered its current status (orders.status_changed_at)
  const formatStatusChanged = (value, withTime) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return withTime
      ? `${day}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
      : day;
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    const symbol = symbolFor(currency);
    if (!amount) return `${symbol}0.00`;
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const handleSaveAwaitingAction = async () => {
    const reason = awaitingReason.trim();
    if (!reason) {
      alert('Please enter a reason');
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/api/update-order-status/${awaitingOrderId}`, {
        status: 'Awaiting Action',
        reason
      });
      setOrders(prev => prev.map(o => o.order_id === awaitingOrderId
        ? { ...o, status: 'Awaiting Action', awaiting_action_reason: reason, status_changed_at: o.status === 'Awaiting Action' ? o.status_changed_at : new Date().toISOString() }
        : o));
      setAwaitingModal(false);
      setAwaitingOrderId(null);
      setAwaitingReason('');
    } catch (err) {
      console.error('Error updating order status:', err);
      alert(`Failed to update status: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    // Awaiting Action needs a reason, so collect it in a modal first
    if (newStatus === 'Awaiting Action') {
      const existing = orders.find(o => o.order_id === orderId);
      setAwaitingOrderId(orderId);
      setAwaitingReason(existing?.awaiting_action_reason || '');
      setAwaitingModal(true);
      return;
    }

    // If changing to "Shipped", open the Mark as Shipped modal instead
    if (newStatus === 'Shipped') {
      const order = orders.find(o => o.order_id === orderId);
      if (order) {
        handleMarkAsShipped(order);
      }
      return;
    }

    try {
      const response = await axios.put(`${API_BASE_URL}/api/update-order-status/${orderId}`, {
        status: newStatus
      });

      if (response.data.message) {
        // Update local state
        const updatedOrders = orders.map(order => {
          if (order.order_id === orderId) {
            return {
              ...order,
              status: newStatus,
              awaiting_action_reason: null,
              status_changed_at: order.status === newStatus ? order.status_changed_at : new Date().toISOString()
            };
          }
          return order;
        });
        setOrders(updatedOrders);
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert(`Failed to update status: ${err.response?.data?.error || err.message}`);
    }
  };

  const getStatusColor = (status) => statusColor(status);

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs mainTitle="All Orders" parent="Orders" />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardBody className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-2">Loading orders...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  }

  if (error) {
    return (
      <Fragment>
        <Breadcrumbs mainTitle="All Orders" parent="Orders" />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardBody className="text-center">
                  <div className="alert alert-danger">
                    <h4>Error</h4>
                    <p>{error}</p>
                    <Button color="primary" onClick={fetchOrders}>
                      Try Again
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs mainTitle="All Orders" parent="Orders" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h5>All Orders</h5>
                    <span>View and manage all customer orders</span>
                  </div>
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    {selectedOrders.length > 0 && (
                      <>
                        <Button
                          color="info"
                          size="sm"
                          onClick={handleOpenPackagingListModal}
                          disabled={packagingLoading}
                        >
                          {packagingLoading ? (
                            <i className="fa fa-spinner fa-spin me-1"></i>
                          ) : (
                            <i className="fa fa-list-alt me-1"></i>
                          )}
                          Packaging List ({selectedOrders.length})
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={toggleShipmentTagModal}
                        >
                          <i className="fa fa-tags me-1"></i>
                          Assign Shipment Tag ({selectedOrders.length})
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          onClick={toggleBulkDepositModal}
                        >
                          <i className="fa fa-plus-circle me-1"></i>
                          Add Deposit ({selectedOrders.length})
                        </Button>
                        <Button
                          color="warning"
                          size="sm"
                          onClick={toggleBulkStatusModal}
                        >
                          <i className="fa fa-refresh me-1"></i>
                          Change Status ({selectedOrders.length})
                        </Button>
                      </>
                    )}
                    <Input
                      type="select"
                      value={shipmentTagFilter}
                      onChange={(e) => setShipmentTagFilter(e.target.value)}
                      style={{ maxWidth: '160px' }}
                    >
                      <option value="All">All Shipments</option>
                      <option value="None">No Shipment Tag</option>
                      {shipmentTags.map(tag => (
                        <option key={tag.id} value={tag.id}>{tag.name} ({tag.month})</option>
                      ))}
                    </Input>
                    <Input
                      type="text"
                      placeholder="Search by Order ID, Store Name, Email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ minWidth: '250px' }}
                    />
                  </div>
                </div>
                <Nav className="order-tabs" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'active' ? 'active' : ''}
                      onClick={() => { setActiveTab('active'); setStatusFilter('All'); }}
                    >
                      Active ({getTabCounts().active})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'cancelled' ? 'active' : ''}
                      onClick={() => { setActiveTab('cancelled'); setStatusFilter('All'); }}
                    >
                      Cancelled ({getTabCounts().cancelled})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'all' ? 'active' : ''}
                      onClick={() => { setActiveTab('all'); setStatusFilter('All'); }}
                    >
                      All ({getTabCounts().all})
                    </NavLink>
                  </NavItem>
                </Nav>
                <div className="status-tabs">
                  <span
                    className={`status-tab status-all ${statusFilter === 'All' ? 'active' : ''}`}
                    onClick={() => setStatusFilter('All')}
                  >
                    All ({getStatusCounts().all})
                  </span>
                  {getStatusCounts()['New Order'] > 0 && (
                    <span
                      className={`status-tab status-new-order ${statusFilter === 'New Order' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('New Order')}
                    >
                      New Order ({getStatusCounts()['New Order']})
                    </span>
                  )}
                  {getStatusCounts()['Awaiting Action'] > 0 && (
                    <button
                      className={`status-tab status-awaiting-action ${statusFilter === 'Awaiting Action' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Awaiting Action')}
                    >
                      Awaiting Action ({getStatusCounts()['Awaiting Action']})
                    </button>
                  )}
                  {getStatusCounts()['Confirmed'] > 0 && (
                    <span
                      className={`status-tab status-confirmed ${statusFilter === 'Confirmed' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Confirmed')}
                    >
                      Confirmed ({getStatusCounts()['Confirmed']})
                    </span>
                  )}
                  {getStatusCounts()['In Production'] > 0 && (
                    <span
                      className={`status-tab status-in-production ${statusFilter === 'In Production' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('In Production')}
                    >
                      In Production ({getStatusCounts()['In Production']})
                    </span>
                  )}
                  {getStatusCounts()['Ready to Ship'] > 0 && (
                    <span
                      className={`status-tab status-ready-to-ship ${statusFilter === 'Ready to Ship' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Ready to Ship')}
                    >
                      Ready to Ship ({getStatusCounts()['Ready to Ship']})
                    </span>
                  )}
                  {getStatusCounts()['Shipped'] > 0 && (
                    <span
                      className={`status-tab status-shipped ${statusFilter === 'Shipped' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Shipped')}
                    >
                      Shipped ({getStatusCounts()['Shipped']})
                    </span>
                  )}
                  {getStatusCounts()['Delivered'] > 0 && (
                    <span
                      className={`status-tab status-delivered ${statusFilter === 'Delivered' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Delivered')}
                    >
                      Delivered ({getStatusCounts()['Delivered']})
                    </span>
                  )}
                  {getStatusCounts()['Deposit Paid'] > 0 && (
                    <span
                      className={`status-tab status-deposit-paid ${statusFilter === 'Deposit Paid' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Deposit Paid')}
                    >
                      Deposit Paid ({getStatusCounts()['Deposit Paid']})
                    </span>
                  )}
                  {getStatusCounts()['Paid in Full'] > 0 && (
                    <span
                      className={`status-tab status-paid-in-full ${statusFilter === 'Paid in Full' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Paid in Full')}
                    >
                      Paid in Full ({getStatusCounts()['Paid in Full']})
                    </span>
                  )}
                  <Input
                    type="select"
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    style={{ maxWidth: '130px', height: '30px', fontSize: '12px', padding: '2px 8px' }}
                    title="Filter by order month"
                  >
                    <option value="All">All Months</option>
                    {MONTH_NAMES.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </Input>
                  <Input
                    type="select"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    style={{ maxWidth: '110px', height: '30px', fontSize: '12px', padding: '2px 8px' }}
                    title="Filter by order year"
                  >
                    <option value="All">All Years</option>
                    {getAvailableYears().map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </Input>
                  {activeTab !== 'active' && getStatusCounts()['Cancelled'] > 0 && (
                    <span
                      className={`status-tab status-cancelled ${statusFilter === 'Cancelled' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('Cancelled')}
                    >
                      Cancelled ({getStatusCounts()['Cancelled']})
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {getFilteredOrders(orders).length === 0 ? (
                  <div className="text-center py-4">
                    <h4>{searchTerm || statusFilter !== 'All' || monthFilter !== 'All' || yearFilter !== 'All' ? 'No Matching Orders' : 'No Orders'}</h4>
                    <p>{searchTerm || statusFilter !== 'All' || monthFilter !== 'All' || yearFilter !== 'All' ? 'No orders match your filter criteria.' : 'No orders found.'}</p>
                    {(searchTerm || statusFilter !== 'All' || shipmentTagFilter !== 'All' || monthFilter !== 'All' || yearFilter !== 'All') && (
                      <Button color="secondary" size="sm" onClick={() => { setSearchTerm(''); setStatusFilter('All'); setShipmentTagFilter('All'); setMonthFilter('All'); setYearFilter('All'); }}>
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <style>{tableStyles}</style>
                    <Table striped hover className="all-orders-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <Input
                              type="checkbox"
                              checked={selectedOrders.length === getFilteredOrders(orders).length && getFilteredOrders(orders).length > 0}
                              onChange={() => handleSelectAll(getFilteredOrders(orders))}
                            />
                          </th>
                          <th className="sortable-header" onClick={() => handleSort('order_id')} style={{ width: '140px' }}>
                            Order ID{getSortIndicator('order_id')}
                          </th>
                          <th className="sortable-header" onClick={() => handleSort('created_at')} style={{ width: '120px' }}>
                            Created At{getSortIndicator('created_at')}
                          </th>
                          <th className="sortable-header" onClick={() => handleSort('store_name')}>
                            Store Name{getSortIndicator('store_name')}
                          </th>
                          <th>Items</th>
                          <th>Total Amount</th>
                          <th>Deposit</th>
                          <th>Shipping</th>
                          <th className="sortable-header" onClick={() => handleSort('status')}>
                            Status{getSortIndicator('status')}
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedOrders(getFilteredOrders(orders)).map((order) => (
                          <tr key={order.order_id} className={selectedOrders.includes(order.order_id) ? 'selected-row' : ''}>
                            <td>
                              <Input
                                type="checkbox"
                                checked={selectedOrders.includes(order.order_id)}
                                onChange={() => handleSelectOrder(order.order_id)}
                              />
                            </td>
                            <td>
                              <div>
                                <a
                                  href={`/faraadmin/collection-order-form/${order.order_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="order-id-link"
                                  style={{
                                    color: '#007bff',
                                    textDecoration: 'none',
                                    fontWeight: 'bold'
                                  }}
                                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {order.order_id}
                                </a>
                                {order.is_immediate === 1 && (
                                  <div className="mt-1">
                                    <Badge color="danger" pill style={{ fontSize: '9px' }}>
                                      Immediate
                                    </Badge>
                                  </div>
                                )}
                                {order.customer_note && (
                                  <div className="mt-1">
                                    <Badge color="info" pill style={{ fontSize: '9px', cursor: 'help' }} title={order.customer_note}>
                                      <i className="fa fa-sticky-note-o me-1"></i>Note
                                    </Badge>
                                  </div>
                                )}
                                {order.shipment_tag_name && (
                                  <div className="mt-1">
                                    <Badge color="dark" pill style={{ fontSize: '9px' }}>
                                      {order.shipment_tag_name} - {order.shipment_tag_month}
                                    </Badge>
                                  </div>
                                )}
                                {order.is_shipped === 1 && (
                                  <div className="mt-1">
                                    <Badge color="success" pill style={{ fontSize: '9px' }}>
                                      Shipped {order.shipped_date ? `on ${formatDate(order.shipped_date)}` : ''}
                                    </Badge>
                                  </div>
                                )}
                                {/* Show discount badge */}
                                {order.discount && (
                                  <div className="mt-1">
                                    <Badge
                                      color="warning"
                                      style={{ cursor: order.is_shipped ? 'default' : 'pointer' }}
                                      onClick={() => !order.is_shipped && handleAddDiscount(order)}
                                      title={order.is_shipped ? 'Discount' : 'Click to edit'}
                                    >
                                      Discount: {order.discount.discount_type === 'percentage'
                                        ? `${order.discount.discount_value}%`
                                        : `${symbolFor(order.currency)}${order.discount.discount_value}`}
                                      {!order.is_shipped && <i className="fa fa-pencil ms-1" style={{ fontSize: '10px' }}></i>}
                                    </Badge>
                                  </div>
                                )}
                                {/* Show action buttons only when not shipped */}
                                {!order.is_shipped && (
                                  <div className="mt-1 d-flex flex-wrap gap-1">
                                    {!order.discount && order.status !== 'In Production' && (
                                      <Button
                                        color="warning"
                                        size="sm"
                                        onClick={() => handleAddDiscount(order)}
                                        title="Add Discount"
                                      >
                                        <i className="fa fa-percent"></i>
                                      </Button>
                                    )}
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleMarkAsShipped(order)}
                                      disabled={shippingOrder === order.order_id}
                                      title="Mark as Shipped"
                                    >
                                      {shippingOrder === order.order_id ? (
                                        <i className="fa fa-spinner fa-spin"></i>
                                      ) : (
                                        <i className="fa fa-truck"></i>
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              {formatDate(order.created_at)}
                            </td>
                            <td>
                              <div>
                                <div className="font-weight-bold">{order.store_name || 'N/A'}</div>
                                {order.email && (
                                  <small className="text-muted">{order.email}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge color="info">{order.item_count} items</Badge>
                            </td>
                            <td>
                              <strong>{formatCurrency(order.total_amount, order.currency)}</strong>
                            </td>
                            {/* Deposit Column */}
                            <td>
                              {order.deposit ? (
                                <div
                                  style={{ cursor: order.is_shipped ? 'default' : 'pointer', textAlign: 'center' }}
                                  onClick={() => !order.is_shipped && handleAddDeposit(order)}
                                >
                                  {order.deposit.deposit_type === 'percentage'
                                    ? (() => {
                                        const subtotal = parseFloat(order.total_amount) - parseFloat(order.shippingCost || 50);
                                        const discountAmount = order.discount
                                          ? (order.discount.discount_type === 'percentage'
                                            ? (parseFloat(order.discount.discount_value) / 100) * subtotal
                                            : parseFloat(order.discount.discount_value))
                                          : 0;
                                        const amountAfterDiscount = subtotal - discountAmount;
                                        const depositAmount = (parseFloat(order.deposit.deposit_value) / 100) * amountAfterDiscount;
                                        return (
                                          <>
                                            <div><strong>{symbolFor(order.currency)}{depositAmount.toFixed(2)}</strong></div>
                                            <Badge color="success" style={{ fontSize: '10px' }}>
                                              {order.deposit.deposit_value}%
                                              {!order.is_shipped && <i className="fa fa-pencil ms-1"></i>}
                                            </Badge>
                                          </>
                                        );
                                      })()
                                    : (
                                      <>
                                        <div><strong>{symbolFor(order.currency)}{order.deposit.deposit_value}</strong></div>
                                        {!order.is_shipped && <i className="fa fa-pencil" style={{ fontSize: '10px' }}></i>}
                                      </>
                                    )}
                                </div>
                              ) : (
                                !order.is_shipped && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleAddDeposit(order)}
                                    title="Add Deposit"
                                  >
                                    <i className="fa fa-plus"></i>
                                  </Button>
                                )
                              )}
                            </td>
                            {/* Shipping Column */}
                            <td>
                              {order.shippingCost ? (
                                <span
                                  style={{ cursor: order.is_shipped ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                                  onClick={() => !order.is_shipped && handleAddShippingCost(order)}
                                  title={order.is_shipped ? 'Shipping cost' : 'Click to edit'}
                                >
                                  <strong>{symbolFor(order.shippingCurrency || order.currency)}{parseFloat(order.shippingCost).toFixed(2)}</strong>
                                  {!order.is_shipped && <i className="fa fa-pencil ms-1" style={{ fontSize: '10px', color: '#6c757d' }}></i>}
                                </span>
                              ) : (
                                !order.is_shipped && (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => handleAddShippingCost(order)}
                                    title="Add Shipping Cost"
                                  >
                                    <i className="fa fa-plus"></i>
                                  </Button>
                                )
                              )}
                            </td>
                            <td>
                              <UncontrolledDropdown>
                                <DropdownToggle
                                  caret
                                  color={getStatusColor(order.status || 'New Order')}
                                  size="sm"
                                  style={{ minWidth: '130px' }}
                                  title={order.status === 'Awaiting Action' ? (order.awaiting_action_reason || '') : undefined}
                                >
                                  {order.status || 'New Order'}
                                </DropdownToggle>
                                <DropdownMenu>
                                  {orderStatuses.map((st) => (
                                    <DropdownItem
                                      key={st.name}
                                      onClick={() => handleStatusChange(order.order_id, st.name)}
                                      active={(order.status || 'New Order') === st.name}
                                    >
                                      <Badge color={st.color} className="me-2">●</Badge>
                                      {st.name}
                                    </DropdownItem>
                                  ))}
                                </DropdownMenu>
                              </UncontrolledDropdown>
                              {order.status_changed_at && (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: '11px', marginTop: '2px' }}
                                  title={`Status changed ${formatStatusChanged(order.status_changed_at, true)}`}
                                >
                                  since {formatStatusChanged(order.status_changed_at, false)}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="btn-group" role="group">
                                {order.status !== 'New Order' && (
                                  <>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => handleDownloadPDF(order.order_id)}
                                      className="me-1"
                                      title="Download PDF PI"
                                    >
                                      <i className="fa fa-file-pdf-o"></i>
                                    </Button>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleDownloadPDFWithHSN(order.order_id)}
                                      className="me-1"
                                      title="Download PDF PI with HSN codes"
                                    >
                                      <i className="fa fa-file-pdf-o"></i> HSN
                                    </Button>
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleOpenReorder(order)}
                                      className="me-1"
                                      title="Reorder Selected SKUs (Generate New PI)"
                                    >
                                      <i className="fa fa-refresh"></i>
                                    </Button>
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleOpenCancelItems(order)}
                                      className="me-1"
                                      title="Cancel Items & Credit Note"
                                    >
                                      <i className="fa fa-ban"></i>
                                    </Button>
                                  </>
                                )}
                                {order.status === 'New Order' && (
                                  <>
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleStatusChange(order.order_id, 'Confirmed')}
                                      className="me-1"
                                      title="Approve Order"
                                    >
                                      <i className="fa fa-check"></i>
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleStatusChange(order.order_id, 'Cancelled')}
                                      className="me-1"
                                      title="Decline Order"
                                    >
                                      <i className="fa fa-times"></i>
                                    </Button>
                                  </>
                                )}
                                {order.status !== 'New Order' && order.is_paid !== 1 && (parseFloat(order.total_paid) || 0) < (parseFloat(order.total_amount) || 0) && (
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleMarkPaid(order)}
                                    className="me-1"
                                    title={(order.total_paid || 0) > 0 ? "Add Payment" : "Mark as Paid"}
                                  >
                                    <i className="fa fa-money"></i>
                                  </Button>
                                )}
                                {order.status !== 'New Order' && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDeleteOrder(order.order_id)}
                                    disabled={deletingOrder === order.order_id}
                                    title="Delete Order"
                                  >
                                    {deletingOrder === order.order_id ? (
                                      <i className="fa fa-spinner fa-spin"></i>
                                    ) : (
                                      <i className="fa fa-trash"></i>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Deposit Modal */}
      <Modal isOpen={depositModal} toggle={toggleDepositModal}>
        <ModalHeader toggle={toggleDepositModal}>
          {selectedOrder?.deposit ? 'Edit' : 'Add'} Deposit for Order {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="depositType">Deposit Type</Label>
              <Input
                type="select"
                name="depositType"
                id="depositType"
                value={depositType}
                onChange={(e) => setDepositType(e.target.value)}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Value ({symbolFor(selectedOrder?.currency)})</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="depositValue">
                {depositType === 'percentage' ? 'Percentage Value' : `Fixed Value (${selectedOrder?.currency || 'EUR'})`}
              </Label>
              <Input
                type="number"
                name="depositValue"
                id="depositValue"
                placeholder={depositType === 'percentage' ? 'Enter percentage (e.g., 30)' : 'Enter amount (e.g., 500)'}
                value={depositValue}
                onChange={(e) => setDepositValue(e.target.value)}
                min="0"
                step={depositType === 'percentage' ? '0.01' : '0.01'}
                max={depositType === 'percentage' ? '100' : undefined}
              />
              {depositType === 'percentage' && depositValue && (
                <small className="text-muted d-block mt-1">
                  Deposit Amount: {symbolFor(selectedOrder?.currency)}{((parseFloat(depositValue) / 100) * parseFloat(selectedOrder?.total_amount || 0)).toFixed(2)}
                </small>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div>
            {selectedOrder?.deposit && (
              <Button color="danger" onClick={handleRemoveDeposit}>
                Remove Deposit
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button color="secondary" onClick={toggleDepositModal}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSaveDeposit}>
              Save Deposit
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Shipping Cost Modal */}
      <Modal isOpen={shippingCostModal} toggle={toggleShippingCostModal}>
        <ModalHeader toggle={toggleShippingCostModal}>
          Add Shipping Cost for Order {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="shippingCostValue">Shipping Cost</Label>
              <div className="d-flex gap-2">
                <Input
                  type="select"
                  name="shippingCostCurrency"
                  id="shippingCostCurrency"
                  value={shippingCostCurrency}
                  onChange={(e) => setShippingCostCurrency(e.target.value)}
                  style={{ maxWidth: '110px' }}
                >
                  {shippingCurrencyOptions(selectedOrder?.currency).map((cur) => (
                    <option key={cur} value={cur}>{symbolFor(cur)} {cur}</option>
                  ))}
                </Input>
                <Input
                  type="number"
                  name="shippingCostValue"
                  id="shippingCostValue"
                  placeholder="Enter shipping cost (e.g., 50.00)"
                  value={shippingCostValue}
                  onChange={(e) => setShippingCostValue(e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              <small className="text-muted d-block mt-1">
                Current shipping cost: {symbolFor(selectedOrder?.shippingCurrency || selectedOrder?.currency)}{parseFloat(selectedOrder?.shippingCost || 50.00).toFixed(2)}
              </small>
              {shippingCostCurrency !== (selectedOrder?.currency || 'EUR') && (
                <small className="text-warning d-block mt-1">
                  This order's goods are priced in {selectedOrder?.currency || 'EUR'}. The shipping line will be
                  shown in {shippingCostCurrency}; no conversion is applied to the order total.
                </small>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleShippingCostModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveShippingCost}>
            Save Shipping Cost
          </Button>
        </ModalFooter>
      </Modal>

      {/* Shipping Modal */}
      <Modal isOpen={shippingModal} toggle={toggleShippingModal}>
        <ModalHeader toggle={toggleShippingModal}>
          Mark Order as Shipped - {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="shippingDate">Shipping Date *</Label>
              <Input
                type="date"
                name="shippingDate"
                id="shippingDate"
                value={shippingDate}
                onChange={(e) => setShippingDate(e.target.value)}
                required
              />
              <small className="text-muted d-block mt-1">
                This will convert the Proforma Invoice to an Invoice.
              </small>
            </FormGroup>
            <FormGroup>
              <Label for="trackingLink">Shipping/Tracking Link (Optional)</Label>
              <Input
                type="url"
                name="trackingLink"
                id="trackingLink"
                placeholder="https://..."
                value={trackingLink}
                onChange={(e) => setTrackingLink(e.target.value)}
              />
              <small className="text-muted">
                Enter the tracking URL from the shipping carrier.
              </small>
            </FormGroup>
            <FormGroup>
              <Label for="shippingDocuments">Shipping Documents (Optional)</Label>
              <Input
                type="file"
                name="shippingDocuments"
                id="shippingDocuments"
                accept="image/*,application/pdf"
                onChange={(e) => setShippingDocuments(Array.from(e.target.files))}
                multiple
              />
              <small className="text-muted">
                Upload shipping documents (images or PDF). Multiple files allowed. These files will be saved to {selectedOrder?.order_id} folder.
              </small>
              {shippingDocuments.length > 0 && (
                <div className="mt-2">
                  <strong>Selected files ({shippingDocuments.length}):</strong>
                  <ul className="mb-0 mt-1">
                    {shippingDocuments.map((file, index) => (
                      <li key={index}><small>{file.name}</small></li>
                    ))}
                  </ul>
                </div>
              )}
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleShippingModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveShipping}>
            Mark as Shipped
          </Button>
        </ModalFooter>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={paymentModal} toggle={togglePaymentModal} size="lg">
        <ModalHeader toggle={togglePaymentModal}>
          Record Payment for Order - {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          <Form>
            {selectedOrder && (
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#7fba00' }}>
                <Row>
                  <Col md={4}>
                    <div className="text-center">
                      <small className="text-white">Order Total</small>
                      <h5 className="text-white mb-0">{formatCurrency(selectedOrder.total_amount, selectedOrder.currency)}</h5>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <small className="text-white">Already Paid</small>
                      <h5 className="text-white mb-0">{formatCurrency(selectedOrder.total_paid || 0, selectedOrder.currency)}</h5>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <small className="text-white">Remaining Balance</small>
                      <h5 className="text-white mb-0">{formatCurrency((selectedOrder.total_amount || 0) - (selectedOrder.total_paid || 0), selectedOrder.currency)}</h5>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="paymentType">Payment Type *</Label>
                  <Input
                    type="select"
                    name="paymentType"
                    id="paymentType"
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    required
                  >
                    <option value="">Select payment type</option>
                    <option value="Deposit">Deposit</option>
                    <option value="Advance">Advance</option>
                    <option value="Installment">Installment</option>
                    <option value="Full Payment">Full Payment</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="paymentAmount">Payment Amount ({selectedOrder ? symbolFor(selectedOrder.currency) : '€'}) *</Label>
                  <Input
                    type="number"
                    name="paymentAmount"
                    id="paymentAmount"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    step="0.01"
                    min="0.01"
                    max={selectedOrder ? (selectedOrder.total_amount || 0) - (selectedOrder.total_paid || 0) : undefined}
                    required
                    placeholder="Enter payment amount"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="paymentDate">Payment Date *</Label>
                  <Input
                    type="date"
                    name="paymentDate"
                    id="paymentDate"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="proofOfPayment">Proof of Payment</Label>
                  <Input
                    type="file"
                    name="proofOfPayment"
                    id="proofOfPayment"
                    accept="image/*,application/pdf"
                    onChange={(e) => setProofOfPayment(e.target.files[0])}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="paymentNotes">Notes</Label>
                  <Input
                    type="textarea"
                    name="paymentNotes"
                    id="paymentNotes"
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    rows="2"
                    placeholder="Add any notes about this payment (optional)"
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={togglePaymentModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSavePayment} disabled={savingPayment}>
            {savingPayment ? 'Saving...' : 'Record Payment'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Modal */}
      <Modal isOpen={discountModal} toggle={toggleDiscountModal}>
        <ModalHeader toggle={toggleDiscountModal}>
          Add Discount for Order {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="discountType">Discount Type</Label>
              <Input
                type="select"
                name="discountType"
                id="discountType"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Value ({symbolFor(selectedOrder?.currency)})</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="discountValue">
                {discountType === 'percentage' ? 'Percentage Value' : `Fixed Value (${selectedOrder?.currency || 'EUR'})`}
              </Label>
              <Input
                type="number"
                name="discountValue"
                id="discountValue"
                placeholder={discountType === 'percentage' ? 'Enter percentage (e.g., 10)' : 'Enter amount (e.g., 100)'}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                min="0"
                step="0.01"
                max={discountType === 'percentage' ? '100' : undefined}
              />
              {discountType === 'percentage' && discountValue && (
                <small className="text-muted d-block mt-1">
                  Discount Amount: {symbolFor(selectedOrder?.currency)}{((parseFloat(discountValue) / 100) * parseFloat(selectedOrder?.total_amount || 0)).toFixed(2)}
                </small>
              )}
              <small className="text-muted d-block mt-1">
                Note: Discount is applied to subtotal before shipping costs.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          {selectedOrder?.discount && (
            <Button color="danger" className="me-auto" onClick={handleRemoveDiscount}>
              Remove Discount
            </Button>
          )}
          <Button color="secondary" onClick={toggleDiscountModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveDiscount}>
            Save Discount
          </Button>
        </ModalFooter>
      </Modal>

      {/* Shipment Tag Modal */}
      <Modal isOpen={shipmentTagModal} toggle={toggleShipmentTagModal}>
        <ModalHeader toggle={toggleShipmentTagModal}>
          Assign Shipment Tag to {selectedOrders.length} Order{selectedOrders.length !== 1 ? 's' : ''}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Selected Orders:</Label>
              <div className="mb-3 p-2 bg-light rounded" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {selectedOrders.map((orderId, idx) => (
                  <Badge key={orderId} color="secondary" className="me-1 mb-1">{orderId}</Badge>
                ))}
              </div>
            </FormGroup>

            {!showNewTagForm ? (
              <>
                <FormGroup>
                  <Label for="shipmentTag">Select Shipment Tag</Label>
                  <Input
                    type="select"
                    name="shipmentTag"
                    id="shipmentTag"
                    value={selectedShipmentTag}
                    onChange={(e) => setSelectedShipmentTag(e.target.value)}
                  >
                    <option value="">-- Remove Shipment Tag --</option>
                    {shipmentTags.map(tag => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name} ({tag.month})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Button
                  color="link"
                  size="sm"
                  onClick={() => setShowNewTagForm(true)}
                  className="p-0"
                >
                  <i className="fa fa-plus me-1"></i> Create New Shipment Tag
                </Button>
              </>
            ) : (
              <>
                <FormGroup>
                  <Label for="newTagName">Shipment Name *</Label>
                  <Input
                    type="text"
                    name="newTagName"
                    id="newTagName"
                    placeholder="e.g., Shipment 1, Shipment 2"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="newTagMonth">Month *</Label>
                  <Input
                    type="text"
                    name="newTagMonth"
                    id="newTagMonth"
                    placeholder="e.g., January 2025, Feb 2025"
                    value={newTagMonth}
                    onChange={(e) => setNewTagMonth(e.target.value)}
                  />
                </FormGroup>
                <div className="d-flex gap-2">
                  <Button color="success" size="sm" onClick={handleCreateNewTag}>
                    Create Tag
                  </Button>
                  <Button color="secondary" size="sm" onClick={() => {
                    setShowNewTagForm(false);
                    setNewTagName('');
                    setNewTagMonth('');
                  }}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleShipmentTagModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleAssignShipmentTag} disabled={showNewTagForm}>
            {selectedShipmentTag ? 'Assign Tag' : 'Remove Tag'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Deposit Modal */}
      <Modal isOpen={bulkDepositModal} toggle={toggleBulkDepositModal}>
        <ModalHeader toggle={toggleBulkDepositModal}>
          Add Deposit to {selectedOrders.length} Order{selectedOrders.length !== 1 ? 's' : ''}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Selected Orders:</Label>
              <div className="mb-3 p-2 bg-light rounded" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {selectedOrders.map((orderId) => (
                  <Badge key={orderId} color="secondary" className="me-1 mb-1">{orderId}</Badge>
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="bulkDepositType">Deposit Type</Label>
              <Input
                type="select"
                name="bulkDepositType"
                id="bulkDepositType"
                value={bulkDepositType}
                onChange={(e) => setBulkDepositType(e.target.value)}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Value</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="bulkDepositValue">
                {bulkDepositType === 'percentage' ? 'Percentage Value' : 'Fixed Value'}
              </Label>
              <Input
                type="number"
                name="bulkDepositValue"
                id="bulkDepositValue"
                placeholder={bulkDepositType === 'percentage' ? 'Enter percentage (e.g., 30)' : 'Enter amount (e.g., 500)'}
                value={bulkDepositValue}
                onChange={(e) => setBulkDepositValue(e.target.value)}
                min="0"
                step="0.01"
                max={bulkDepositType === 'percentage' ? '100' : undefined}
              />
              <small className="text-muted d-block mt-1">
                This deposit will be applied to all {selectedOrders.length} selected order(s).
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleBulkDepositModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveBulkDeposit}>
            Save Deposit
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bulk Status Modal */}
      <Modal isOpen={awaitingModal} toggle={() => setAwaitingModal(false)}>
        <ModalHeader toggle={() => setAwaitingModal(false)}>
          Awaiting Action - Order {awaitingOrderId}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="awaitingReason">Please provide the reason this order has not moved to In Production.</Label>
            <Input
              type="textarea"
              id="awaitingReason"
              rows="4"
              value={awaitingReason}
              onChange={(e) => setAwaitingReason(e.target.value)}
              placeholder="Enter the reason..."
              autoFocus
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAwaitingModal(false)}>Cancel</Button>
          <Button color="primary" onClick={handleSaveAwaitingAction} disabled={!awaitingReason.trim()}>Save</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={bulkStatusModal} toggle={toggleBulkStatusModal}>
        <ModalHeader toggle={toggleBulkStatusModal}>
          Change Status for {selectedOrders.length} Order{selectedOrders.length !== 1 ? 's' : ''}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Selected Orders:</Label>
              <div className="mb-3 p-2 bg-light rounded" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {selectedOrders.map((orderId) => (
                  <Badge key={orderId} color="secondary" className="me-1 mb-1">{orderId}</Badge>
                ))}
              </div>
            </FormGroup>
            <FormGroup>
              <Label for="bulkStatusValue">Select Status</Label>
              <Input
                type="select"
                name="bulkStatusValue"
                id="bulkStatusValue"
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
              >
                <option value="">-- Select Status --</option>
                {orderStatuses.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </Input>
              <small className="text-muted d-block mt-1">
                This status will be applied to all {selectedOrders.length} selected order(s).
              </small>
            </FormGroup>
            {bulkStatusValue === 'Awaiting Action' && (
              <FormGroup>
                <Label for="bulkAwaitingReason">Reason this order has not moved to In Production</Label>
                <Input
                  type="textarea"
                  id="bulkAwaitingReason"
                  rows="3"
                  value={bulkAwaitingReason}
                  onChange={(e) => setBulkAwaitingReason(e.target.value)}
                />
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleBulkStatusModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveBulkStatus}>
            Update Status
          </Button>
        </ModalFooter>
      </Modal>

      {/* Packaging List Modal */}
      <Modal isOpen={packagingListModal} toggle={togglePackagingListModal} size="xl">
        <ModalHeader toggle={togglePackagingListModal}>
          Generate Packaging Lists for {selectedOrders.length} Order(s)
        </ModalHeader>
        <ModalBody>
          {packagingPreview && (
            <>
              {/* Summary */}
              <div className="d-flex gap-4 mb-4 flex-wrap">
                <div className="p-3 bg-light rounded" style={{ minWidth: '150px' }}>
                  <div className="text-muted small">Total Orders</div>
                  <div className="h4 mb-0">{packagingPreview.summary.total_orders}</div>
                </div>
                <div className="p-3 bg-light rounded" style={{ minWidth: '150px' }}>
                  <div className="text-muted small">Total SKUs</div>
                  <div className="h4 mb-0">{packagingPreview.summary.total_skus}</div>
                </div>
                <div className="p-3 bg-light rounded" style={{ minWidth: '150px' }}>
                  <div className="text-muted small">Items Needed</div>
                  <div className="h4 mb-0">{packagingPreview.summary.total_items_needed}</div>
                </div>
                <div className="p-3 bg-success text-white rounded" style={{ minWidth: '150px' }}>
                  <div className="small opacity-75">Allocated</div>
                  <div className="h4 mb-0">{packagingPreview.summary.total_allocated}</div>
                </div>
                {packagingPreview.summary.total_unfulfilled > 0 && (
                  <div className="p-3 bg-danger text-white rounded" style={{ minWidth: '150px' }}>
                    <div className="small opacity-75">Unfulfilled</div>
                    <div className="h4 mb-0">{packagingPreview.summary.total_unfulfilled}</div>
                  </div>
                )}
              </div>

              {/* Unfulfilled Warning */}
              {packagingPreview.unfulfilled.length > 0 && (
                <div className="alert alert-warning mb-4">
                  <strong><i className="fa fa-exclamation-triangle me-2"></i>Insufficient Stock Warning</strong>
                  <p className="mb-2 mt-2">The following items cannot be fully allocated due to insufficient stock:</p>
                  <ul className="mb-0" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {packagingPreview.unfulfilled.map((item, idx) => (
                      <li key={idx}>
                        {item.title} ({item.sku}) - Size: {item.size} - Missing: {item.quantity_unfulfilled} units
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* SKU Summary Table */}
              <h6 className="mb-3">SKU Allocation Summary</h6>
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table bordered size="sm">
                  <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                    <tr>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Size</th>
                      <th>Needed</th>
                      {packagingPreview.stores.map(store => (
                        <th key={store.id} className="text-center" style={{ minWidth: '80px' }}>
                          {store.name}
                          <br />
                          <small className="text-muted">(Stock)</small>
                        </th>
                      ))}
                      <th className="text-center">Allocated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packagingPreview.skuItems.map((item, idx) => (
                      <tr key={idx} className={item.quantity_unfulfilled > 0 ? 'table-warning' : ''}>
                        <td><code>{item.sku}</code></td>
                        <td>
                          <div>{item.title}</div>
                          {item.colour && <small className="text-muted">{item.colour}</small>}
                        </td>
                        <td>{item.size}</td>
                        <td className="text-center"><strong>{item.total_quantity}</strong></td>
                        {packagingPreview.stores.map(store => {
                          const stock = item.storeStock[store.id] || 0;
                          const allocation = item.allocations?.find(a => a.store_id === store.id);
                          return (
                            <td key={store.id} className="text-center">
                              <div>{stock}</div>
                              {allocation && (
                                <Badge color="success" pill style={{ fontSize: '10px' }}>
                                  -{allocation.quantity}
                                </Badge>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center">
                          <Badge color={item.quantity_unfulfilled > 0 ? 'warning' : 'success'}>
                            {item.quantity_allocated} / {item.total_quantity}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Store Allocation Summary */}
              <h6 className="mt-4 mb-3">Store Allocation Summary</h6>
              <div className="d-flex gap-3 flex-wrap">
                {packagingPreview.stores.map(store => {
                  const storeItems = packagingPreview.allocations[store.id] || [];
                  const totalQty = storeItems.reduce((sum, item) => sum + item.quantity_allocated, 0);
                  return (
                    <div key={store.id} className="p-3 border rounded" style={{ minWidth: '180px' }}>
                      <div className="fw-bold">{store.name}</div>
                      <div className="text-muted small">{storeItems.length} SKUs</div>
                      <div className="h5 mb-0 mt-2">
                        {totalQty > 0 ? (
                          <Badge color="primary" style={{ fontSize: '14px' }}>{totalQty} items</Badge>
                        ) : (
                          <span className="text-muted">No items</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={togglePackagingListModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleGeneratePackagingLists}
            disabled={generatingPdfs || !packagingPreview || packagingPreview.summary.total_allocated === 0}
          >
            {generatingPdfs ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Generating PDFs...
              </>
            ) : (
              <>
                <i className="fa fa-file-pdf-o me-2"></i>
                Generate Packaging List PDFs
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Cancel Items & Credit Note Modal */}
      <Modal isOpen={cancelItemsModal} toggle={toggleCancelItemsModal} size="xl" style={{ maxWidth: '95vw' }}>
        <ModalHeader toggle={toggleCancelItemsModal}>
          Cancel Items & Generate Credit Note — {selectedOrder?.order_id}
        </ModalHeader>
        <ModalBody>
          {cancelItemsLoading ? (
            <div className="text-center py-4">
              <i className="fa fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2">Loading order items...</p>
            </div>
          ) : cancelOrderItems.length === 0 ? (
            <div className="text-center py-4">
              <p>No items available for cancellation.</p>
            </div>
          ) : (
            <>
              <p className="text-muted mb-3">
                Type the quantity to cancel for any SKU directly in the <strong>Cancel Qty</strong> column
                (ticking the checkbox defaults to the full available qty). Leave the box blank/0 to skip
                that SKU. The credit note covers exactly the quantities you enter.
              </p>
              <div className="table-responsive">
                <Table bordered striped size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Input
                          type="checkbox"
                          checked={cancelOrderItems.length > 0 && cancelOrderItems.every(item => selectedCancelItems[`${item.product_id}_${item.size}`]?.selected)}
                          onChange={handleToggleAllCancelItems}
                        />
                      </th>
                      <th>Title</th>
                      <th>Colour</th>
                      <th>SKU</th>
                      <th>Size</th>
                      <th style={{ width: '120px', backgroundColor: '#fff3cd' }}>Cancel Qty</th>
                      <th>Available</th>
                      <th>Orig Qty</th>
                      <th>Cancelled</th>
                      <th>Collection</th>
                      <th>Fabric</th>
                      <th>Unit Price</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cancelOrderItems.map((item) => {
                      const key = `${item.product_id}_${item.size}`;
                      const isSelected = selectedCancelItems[key]?.selected;
                      const cancelQty = selectedCancelItems[key]?.cancel_qty ?? (parseInt(item.available_qty, 10) || 1);
                      const unitPrice = parseFloat(item.wholesale_price) || 0;

                      return (
                        <tr key={key} className={isSelected ? 'table-warning' : ''}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggleCancelItem(item.product_id, item.size)}
                            />
                          </td>
                          <td>{item.title}</td>
                          <td>{item.colour || ''}</td>
                          <td>{item.sku}</td>
                          <td>{item.size}</td>
                          <td style={{ backgroundColor: '#fffbe6' }}>
                            <Input
                              type="number"
                              bsSize="sm"
                              min={0}
                              max={item.available_qty}
                              placeholder="0"
                              value={isSelected ? cancelQty : ''}
                              onChange={(e) => handleCancelQtyChange(item.product_id, item.size, e.target.value)}
                              style={{ width: '90px', fontWeight: 'bold' }}
                            />
                          </td>
                          <td>{item.available_qty}</td>
                          <td>{item.quantity}</td>
                          <td>{item.cancelled_qty || 0}</td>
                          <td>{item.collection || ''}</td>
                          <td>{item.fabric || ''}</td>
                          <td>€{unitPrice.toFixed(2)}</td>
                          <td>{isSelected ? `€${(unitPrice * cancelQty).toFixed(2)}` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <div className="text-end p-3" style={{ background: '#f8f9fa', borderRadius: '6px', minWidth: '300px' }}>
                  <div className="mb-1"><strong>Items to cancel:</strong> {getCreditTotal().count}</div>
                  <div className="mb-1">Items subtotal: €{getCreditTotal().total.toFixed(2)}</div>
                  <div className="d-flex align-items-center justify-content-end mb-2" style={{ gap: '8px' }}>
                    <label className="mb-0" style={{ fontSize: '13px' }}>Shipping (optional):</label>
                    <Input
                      type="number"
                      bsSize="sm"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={creditNoteShipping}
                      onChange={(e) => setCreditNoteShipping(e.target.value)}
                      style={{ width: '110px' }}
                    />
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#d9534f' }}>
                    Total Credit: €{(getCreditTotal().total + (parseFloat(creditNoteShipping) || 0)).toFixed(2)}
                  </div>
                  <small className="text-muted">Shipping applies only to credit notes.</small>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleCancelItemsModal}>Close</Button>
          <Button
            color="warning"
            onClick={handleCancelItemsOnly}
            disabled={cancelItemsGenerating || Object.values(selectedCancelItems).filter(v => v.selected).length === 0}
            title="Cancel the selected items without creating a credit note (use for unpaid orders)"
          >
            {cancelItemsGenerating ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Cancelling...
              </>
            ) : (
              <>
                <i className="fa fa-ban me-2"></i>
                Cancel Items Only
              </>
            )}
          </Button>
          <Button
            color="danger"
            onClick={handleGenerateCreditNote}
            disabled={cancelItemsGenerating || Object.values(selectedCancelItems).filter(v => v.selected).length === 0}
            title="Cancel the selected items and generate a credit note PDF"
          >
            {cancelItemsGenerating ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="fa fa-file-pdf-o me-2"></i>
                Cancel & Generate Credit Note
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reorder Modal */}
      <Modal isOpen={reorderModal} toggle={toggleReorderModal} size="xl" style={{ maxWidth: '95vw' }}>
        <ModalHeader toggle={toggleReorderModal}>
          Reorder from {selectedOrder?.order_id} — {selectedOrder?.store_name || ''}
        </ModalHeader>
        <ModalBody>
          {reorderLoading ? (
            <div className="text-center py-4">
              <i className="fa fa-spinner fa-spin fa-2x"></i>
              <p className="mt-2">Loading order items...</p>
            </div>
          ) : reorderItems.length === 0 ? (
            <div className="text-center py-4">
              <p>No items found in this order.</p>
            </div>
          ) : (
            <>
              <p className="text-muted mb-3">
                Type the quantity to reorder for any SKU directly in the <strong>Reorder Qty</strong> column
                (or tick the checkbox to default to the original qty). Leave blank/0 to skip. A new order will be
                created and the PI will open in a new tab.
              </p>
              <div className="table-responsive">
                <Table bordered striped size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Input
                          type="checkbox"
                          checked={reorderItems.length > 0 && reorderItems.every(item => selectedReorderItems[`${item.product_id}_${item.size}`]?.selected)}
                          onChange={handleToggleAllReorderItems}
                        />
                      </th>
                      <th>Title</th>
                      <th>Colour</th>
                      <th>SKU</th>
                      <th>Size</th>
                      <th style={{ width: '120px', backgroundColor: '#d4edda' }}>Reorder Qty</th>
                      <th>Original Qty</th>
                      <th>Collection</th>
                      <th>Fabric</th>
                      <th>Unit Price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reorderItems.map((item) => {
                      const key = `${item.product_id}_${item.size}`;
                      const isSelected = selectedReorderItems[key]?.selected;
                      const reorderQty = selectedReorderItems[key]?.reorder_qty || item.quantity || 1;
                      const unitPrice = reorderUnitPrice(item, selectedOrder?.currency);
                      const symbol = symbolFor(selectedOrder?.currency);

                      return (
                        <tr key={key} className={isSelected ? 'table-success' : ''}>
                          <td>
                            <Input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={() => handleToggleReorderItem(item.product_id, item.size)}
                            />
                          </td>
                          <td>{item.title}</td>
                          <td>{item.colour || ''}</td>
                          <td>{item.sku}</td>
                          <td>{item.size}</td>
                          <td style={{ backgroundColor: '#eaffea' }}>
                            <Input
                              type="number"
                              bsSize="sm"
                              min={0}
                              placeholder="0"
                              value={isSelected ? reorderQty : ''}
                              onChange={(e) => handleReorderQtyChange(item.product_id, item.size, e.target.value)}
                              style={{ width: '90px', fontWeight: 'bold' }}
                            />
                          </td>
                          <td>{item.quantity}</td>
                          <td>{item.collection || ''}</td>
                          <td>{item.fabric || ''}</td>
                          <td>{symbol}{unitPrice.toFixed(2)}</td>
                          <td>{isSelected ? `${symbol}${(unitPrice * reorderQty).toFixed(2)}` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <div className="text-end p-3" style={{ background: '#f8f9fa', borderRadius: '6px', minWidth: '250px' }}>
                  <div className="mb-1"><strong>Items to reorder:</strong> {getReorderTotal().count}</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                    Reorder Total: {symbolFor(selectedOrder?.currency)}{getReorderTotal().total.toFixed(2)}
                  </div>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleReorderModal}>Close</Button>
          <Button
            color="success"
            onClick={handleGenerateReorder}
            disabled={reorderGenerating || Object.values(selectedReorderItems).filter(v => v.selected).length === 0}
          >
            {reorderGenerating ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Creating Reorder...
              </>
            ) : (
              <>
                <i className="fa fa-file-pdf-o me-2"></i>
                Create Reorder & Generate PI
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AllOrders;
